import React, { useMemo } from "react";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import withPageFilters from "../../Layout/withPageFilters";
import Loader from "../../components/Loader/Loader";
import EmptyState from "../../components/EmptyState";
import ThemedTooltip from "../../components/Charts/ThemedTooltip";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useGetStatsQuery } from "../../store/api/callsApi";
import { useGetConversionReportQuery } from "../../store/api/reportsApi";
import { useClearDispositionFilterOnMount } from "../../hooks/useClearDispositionFilterOnMount";

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--chart-6)",
  "var(--chart-7)",
];

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  boxShadow: "var(--shadow-card)",
};

const KPI_CONFIG = [
  { key: "totalCalls", label: "Total Calls", format: (v) => v.toLocaleString() },
  { key: "sales", label: "Sales", format: (v) => v.toLocaleString() },
  { key: "conversionRate", label: "Conversion Rate", format: (v) => `${v.toFixed(1)}%` },
  { key: "notQualified", label: "Not Qualified", format: (v) => v.toLocaleString() },
];

const KpiCard = ({ label, value }) => (
  <div className="col">
    <div style={cardStyle} className="p-3 h-100">
      <div className="small" style={{ color: "var(--text-secondary)" }}>
        {label}
      </div>
      <div className="mt-1" style={{ fontSize: "1.6rem", fontWeight: 600, color: "var(--text-primary)" }}>
        {value}
      </div>
    </div>
  </div>
);

const Analytics = () => {
  useClearDispositionFilterOnMount();

  const queryParams = useQueryParams();

  const { data: stats, error: statsError, isLoading: statsLoading } = useGetStatsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });
  const { data: report, error: reportError, isLoading: reportLoading } = useGetConversionReportQuery(
    queryParams,
    { refetchOnMountOrArgChange: true }
  );

  React.useEffect(() => {
    if (statsError) toast.error("Failed to load analytics stats");
    if (reportError) toast.error("Failed to load analytics trend/publisher data");
  }, [statsError, reportError]);

  const dispositionData = useMemo(
    () =>
      (stats?.dispositions || [])
        .filter((d) => d.count > 0)
        .map((d) => ({ name: d.disposition, value: d.count })),
    [stats]
  );

  const trendData = useMemo(() => {
    const map = new Map();
    (report?.trend || []).forEach((row) => {
      const existing = map.get(row.period) || { period: row.period, totalCalls: 0, sales: 0 };
      existing.totalCalls += row.totalCalls || 0;
      existing.sales += row.sales || 0;
      map.set(row.period, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  }, [report]);

  const publisherData = useMemo(
    () =>
      [...(report?.summary || [])]
        .sort((a, b) => b.totalCalls - a.totalCalls)
        .slice(0, 8)
        .map((row) => ({
          publisher: row.publisher,
          totalCalls: row.totalCalls,
          sales: row.sales,
        })),
    [report]
  );

  const kpis = useMemo(() => {
    const totalCalls = stats?.totalCalls || 0;
    const sales = stats?.dispositions?.find((d) => d.disposition === "Sales")?.count || 0;
    const notQualified =
      stats?.dispositions?.find((d) => d.disposition === "Not Qualified")?.count || 0;
    const conversionRate = totalCalls > 0 ? (sales / totalCalls) * 100 : 0;
    return { totalCalls, sales, conversionRate, notQualified };
  }, [stats]);

  const isLoading = statsLoading || reportLoading;

  if (isLoading) return <Loader message="Building analytics..." />;

  const hasData = (stats?.totalCalls || 0) > 0;

  return (
    <div className="mt-2">
      <h5 className="mb-3" style={{ color: "var(--text-primary)" }}>
        Analytics
      </h5>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3 mb-4">
        {KPI_CONFIG.map((cfg) => (
          <KpiCard key={cfg.key} label={cfg.label} value={cfg.format(kpis[cfg.key])} />
        ))}
      </div>

      {!hasData ? (
        <EmptyState message="No calls found for the selected filters." />
      ) : (
        <>
          <div className="row g-3 mb-3">
            <div className="col-12 col-lg-8">
              <div style={cardStyle} className="p-3 h-100">
                <div className="mb-2" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                  Call Volume Trend
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="totalCallsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--chart-3)" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="var(--chart-3)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="period" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                    <YAxis stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                    <Tooltip content={<ThemedTooltip />} />
                    <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="totalCalls"
                      name="Total Calls"
                      stroke="var(--chart-1)"
                      fill="url(#totalCallsGradient)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      name="Sales"
                      stroke="var(--chart-3)"
                      fill="url(#salesGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div style={cardStyle} className="p-3 h-100">
                <div className="mb-2" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                  Disposition Breakdown
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={dispositionData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {dispositionData.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ThemedTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="d-flex flex-wrap gap-2 mt-2 justify-content-center">
                  {dispositionData.map((entry, index) => (
                    <span key={entry.name} className="small d-flex align-items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        }}
                      />
                      {entry.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12">
              <div style={cardStyle} className="p-3">
                <div className="mb-2" style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                  Top Publishers by Volume
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={publisherData} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" stroke="var(--text-secondary)" tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="publisher"
                      stroke="var(--text-secondary)"
                      tick={{ fontSize: 12 }}
                      width={140}
                    />
                    <Tooltip content={<ThemedTooltip />} />
                    <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12 }} />
                    <Bar dataKey="totalCalls" name="Total Calls" fill="var(--chart-1)" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="sales" name="Sales" fill="var(--chart-3)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default withPageFilters(Analytics);
